// @ts-nocheck
// eslint-disable
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { trackOnboarding } from '../utils/analytics';

// Helper function to convert simple markdown to HTML
const parseMessageToHTML = (message) => {
  return message
    // Bold: **text** or __text__
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.*?)__/g, '<strong>$1</strong>')
    // Italic: *text* or _text_
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/_(.*?)_/g, '<em>$1</em>')
    // Code: `text`
    .replace(/`(.*?)`/g, '<code>$1</code>')
    // Line breaks
    .replace(/\n/g, '<br/>');
};

const OnboardingTour = ({ isActive, onComplete, onSkip }) => {
  // Define your milestones
  const MILESTONES = {
    1: 'onboarding_started',
    7: 'open_add_users_modal',
    11: 'add_user_to_all_domain_subproperties',
    12: 'remove_user_table_button',
    14: 'bulk_remove_users-informed_removal_limitations',
    16: 'open_backlog',
    17: 'backlog_job_remove',
    27: 'toggle_known_host',
    29: 'onboard_profile_settings',
    34: 'finished_onboarding'
  };

  // Steps that require click to auto-advance
  const CLICK_TO_ADVANCE_STEPS = [6, 10, 15, 19, 23, 28];

  const [currentStep, setCurrentStep] = useState(() => {
    const savedStep = localStorage.getItem('onboardingCurrentStep');
    const step = savedStep ? parseInt(savedStep) : 0;
    console.log('[Onboarding] Initializing with step:', step);
    return step;
  });

  const [highlightedElement, setHighlightedElement] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: window.innerWidth / 2 - 200, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [pageReady, setPageReady] = useState(false);

  // When onboarding starts
  useEffect(() => {
    if (isActive && currentStep === 0) {
      trackOnboarding('started', 'onboarding_started', 1);
    }
  }, [isActive, currentStep]);

  // Wait for page to be fully ready
  useEffect(() => {
    console.log('[Onboarding] Waiting for page to be ready...');
    
    const waitForPageReady = async () => {
      await waitForLoader();
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('[Onboarding] Page is ready!');
      setPageReady(true);
    };
    
    waitForPageReady();
  }, []);

  // Onboarding steps configuration
  const steps = [
    {
      id: 1,
      page: '/user-management',
      title: 'Welcome to LostLogic',
      message: `Welcome to LostLogic!
    
This quick walkthrough will show you how to use the tool. You can drag this window if it gets in your way.

You can restart this tour anytime from your profile settings.`,
      target: '#list-user-table, .table',
    },
    
    {
      id: 2,
      page: '/user-management',
      title: 'User Management Table',
      message: `This is your central user management table. View all users and their permissions across all GSC properties.

When you add or remove users, you're scheduling changes to run automatically every Saturday.`,
      target: '#list-user-table, .table',
    },
    
    {
      id: 3,
      page: '/user-management',
      title: 'Scheduling Rules',
      message: `Each user/property combination can only be scheduled once per week.

To modify a scheduled change (e.g., adjust permission level), remove it from the backlog and re-add it with the correct settings.`,
      target: '#list-user-table, .table',
    },
    
    {
      id: 4,
      page: '/user-management',
      title: 'Date Picker',
      message: 'View historical snapshots of user permissions using this date picker.',
      target: '#list-user-dateSelector, input[type="date"], .date-picker, input[value*="2025"]',
    },
    
    {
      id: 5,
      page: '/user-management',
      title: 'Filter Controls',
      message: `Filter by email, GSC domain, property, or permission level.

Useful for auditing specific users or investigating access patterns.`,
      target: '#list-user-filters, .filter-row, input[placeholder*="Email"], input[placeholder*="GSC"]',
    },
    
    {
      id: 6,
      page: '/user-management',
      title: 'Add Users',
      message: `Let's walk through adding users with practice data.

When you see **[CLICK]**, interact with the highlighted element.

**[CLICK]** the "Add Users" button.`,
      target: '#list-user-button, button:contains("ADD USERS"), .btn-success',
      requiresClick: true,
    },
    
    {
      id: 7,
      page: '/user-management',
      title: 'Email Input',
      message: `Enter Google Account email addresses—one per line.
      
      As part of onboarding, fill in one Google Account address to add.`,
      target: '#add-users-modal',
      requiresModal: true,
      requiresModalOpen: true,
      modalTrigger: 'textarea',
    },
    
    {
      id: 8,
      page: '/user-management',
      title: 'Permission Level',
      message: 'Select the permission level to grant. All listed users will receive the same permissions.',
      target: '#permissions',
      requiresModal: true,
    },
    
    {
      id: 9,
      page: '/user-management',
      title: 'Property Scope',
      message: `Choose where to grant access:

**All GSC Properties** - Every property connected to LostLogic

**All properties of one domain** - All variants of a single domain
Example: Selecting "mypage.es" grants access to:
- sc-domain:mypage.es
- http://mypage.es
- http://www.mypage.es
- https://mypage.es
- https://www.mypage.es

**Single property** - One specific property (e.g., https://www.mypage.es)`,
      target: '#properties, .modal .form-check, .modal input[type="radio"]',
      requiresModal: true,
    },
    
    {
      id: 10,
      page: '/user-management',
      title: 'Exercise',
      message: `For this tutorial, **[CLICK]** "All properties of one domain"`,
      target: '#add_all-properties-of-1-domain',
      requiresModal: true,
      requiresClick: true,
    },
    
    {
      id: 11,
      page: '/user-management',
      title: 'Complete Form',
      message: `Complete the form for the exercise:
1. Select a domain from the dropdown
2. Choose a permission level
3. Enter one valid Google Account email

At last - click "Add Users" button.

This schedules the change for Saturday.`,
      target: '#properties-dropdown, .modal select:last-of-type, .modal .form-select:last-of-type, input[type="radio"]:nth-of-type(2)',
      requiresModal: true,
    },
    
    {
      id: 12,
      page: '/user-management',
      title: 'Remove Actions',
      message: `To remove users who currently have access, **[CLICK]** the "Remove" button.

You can remove this user from:
- **Property** - This specific property only
- **Domain** - All properties of this domain
- **Account** - All properties in LostLogic`,
      target: 'td:last-child, .remove-column',
      requiresClick: false,
    },
    
    {
      id: 13,
      page: '/user-management',
      title: 'Bulk Removal',
      message: `Alternatively, filter the table to show target users, then select specific user/property combinations using checkboxes.

**For this exercise:** Select 2 combinations, then click Next.`,
      target: 'input[type="checkbox"], td:first-child',
    },
    
    {
      id: 14,
      page: '/user-management',
      title: 'Execute Bulk Removal',
      message: `**[CLICK]** "Remove from properties"

**Limitations:**
- You can't remove Owner Verified users (remove their verification token first)
- You can't remove LostLogic accounts (this is Google's limitation)`,
      target: '#remove-from-properties-btn, button:contains("REMOVE FROM PROPERTIES"), .bulk-remove-btn, button[class*="remove"]',
    },
    
    {
      id: 15,
      page: '/user-management',
      title: 'Navigate to Reporting',
      message: `All add/remove actions are scheduled for Saturday execution in Backlog.

To view or modify the backlog, **[CLICK]** Reporting.`,
      target: 'a[href="/reporting"], nav a:contains("REPORTING")',
      requiresClick: true,
    },
    
    {
      id: 16,
      page: '/reporting',
      title: 'Backlog Tab',
      message: `The Backlog shows all add/remove operations scheduled for execution.

Each user/property combination can only be scheduled once per week.`,
      target: '#reporting-nav, .nav-tabs .nav-link:first-child, button:contains("Backlog")',
    },
    
    {
      id: 17,
      page: '/reporting',
      title: 'Modify Backlog',
      message: `To modify a scheduled operation, click the × to remove it, then re-add with correct settings.

**For this exercise:** Remove all practice changes you scheduled.`,
      target: 'table tbody tr:first-child button, table tbody tr:first-child .remove-btn, tbody button:first-of-type',
    },
    
    {
      id: 18,
      page: '/reporting',
      title: 'Log Tab',
      message: `The Log tab shows all successfully executed operations.

Failed operations appear in the Errors tab.`,
      target: '#reporting-nav2, .nav-tabs .nav-link:first-child, button:contains("Backlog")',
    },
    
    {
      id: 19,
      page: '/reporting',
      title: 'Errors Tab',
      message: `The Errors tab shows:
- User add/remove failures
- Properties where LostLogic lacks Owner permissions

Check this after your first user listing to identify access issues.

**[CLICK]** the Errors tab to continue, or skip ahead if no errors are present.`,
      target: '#reporting-nav3, .nav-tabs .nav-link:first-child, button:contains("Backlog")',
      requiresClick: true,
      isNextDisabled: true,
      skipOption: {
        targetStep: 22, // Step 23 (Navigate to Host Monitoring) - array index 22
        samePageSkip: true,
        buttonText: 'Skip Error Details'
      }
    },
    
    {
      id: 20,
      page: '/reporting',
      title: 'User Management Errors',
      message: 'This table shows errors encountered when adding or removing users.',
      target: '#jobError',
    },
    
    {
      id: 21,
      page: '/reporting',
      title: 'Error Details',
      message: `Check the 'Issue' column for the specific reason each operation failed.`,
      target: '#jobissue',
    },
    
    {
      id: 22,
      page: '/reporting',
      title: 'Access Errors',
      message: `This table shows properties where LostLogic lacks Owner permissions.

Grant Owner access to these properties to enable user management.`,
      target: 'h4:contains("Access Errors"), .access-errors, #access-error-table',
    },
    
    {
      id: 23,
      page: '/reporting',
      title: 'Navigate to Host Monitoring',
      message: `**[CLICK]** Host Monitoring in the navigation.`,
      target: 'a[href="/host-monitoring"], nav a:contains("HOST MONITORING")',
      requiresClick: true,
    },
    
    {
      id: 24,
      page: '/host-monitoring',
      title: 'Discovered Hosts',
      message: `This table shows all hosts (subdomains) discovered across your domains.

Review for unknown or forgotten subdomains that could indicate security issues or spam attacks.`,
      target: '.table',
    },
    
    {
      id: 25,
      page: '/host-monitoring',
      title: 'Domain Filter',
      message: 'Filter the hosts table by selecting a specific domain.',
      target: '#domainSelector',
    },
    
    {
      id: 26,
      page: '/host-monitoring',
      title: 'Date Filter',
      message: 'Browse historical host discovery reports using this date picker.',
      target: 'input[type="date"]:last-of-type, .date-picker, input[value*="2025"]',
    },
    
    {
      id: 27,
      page: '/host-monitoring',
      title: 'Known Hosts Toggle',
      message: `Mark legitimate hosts as "Known" to make unknown hosts easier to spot (You can also sort by "Known" column).`,
      target: 'table tbody tr:first-child .custom-toggle-switch',
    },
    
    {
      id: 28,
      page: '/host-monitoring',
      title: 'Profile Settings',
      message: '**[CLICK]** the profile icon to access account settings.',
      target: 'a[href="/profile"], .profile-icon, i.bi-person-circle',
      requiresClick: true,
    },
    
    {
      id: 29,
      page: '/profile',
      title: 'Account Status',
      message: `View contract status, expiration date, user limits, and Rogue Monitoring status.

Rogue Monitoring sends email alerts when new Owner Verified users or hosts are detected.`,
      target: '.card:contains("Account Status"), .account-status-card',
    },
    
    {
      id: 30,
      page: '/profile',
      title: 'Usage Statistics',
      message: 'View the number of domains, properties, and unique users currently managed.',
      target: '.card:contains("Usage Statistics"), .usage-stats-card',
    },
    
    {
      id: 31,
      page: '/profile',
      title: 'Success Notifications',
      message: `Toggle success notifications when scheduling add/remove operations in the "Manage GSC Users" tab.`,
      target: 'label.custom-toggle-switch, .toggle-rogue',
    },
    
    {
      id: 32,
      page: '/profile',
      title: 'Rogue Monitoring Alerts',
      message: `Enable email notifications for new hosts or verified users.

Requires Rogue Monitoring to be active in Account Status.`,
      target: '#RogueMonitoring.custom-toggle-switch',
    },
    
    {
      id: 33,
      page: '/profile',
      title: 'Organization Users',
      message: 'Admins can add or remove organization members here.',
      target: '.card:contains("Organization Users"), .org-users-table',
    },
    
    {
      id: 34,
      page: '/profile',
      title: 'Tour Complete',
      message: `You've completed the LostLogic tour!

You can restart this tour anytime from your profile settings.`
    }
  ];

  // Check if onboarding should start
  useEffect(() => {
    const hasCompletedOnboarding = localStorage.getItem('onboardingCompleted');
    const savedStep = localStorage.getItem('onboardingCurrentStep');
    
    if (!hasCompletedOnboarding && isActive) {
      if (savedStep) {
        setCurrentStep(parseInt(savedStep));
      } else {
        setCurrentStep(0);
      }
    }
  }, [isActive]);

  // Handle click-to-advance with page reload support
  useEffect(() => {
    if (!isActive || !highlightedElement) return;
    
    const step = steps[currentStep];
    const stepNumber = currentStep + 1;
    
    // Only add click listener for steps that should auto-advance
    if (!CLICK_TO_ADVANCE_STEPS.includes(stepNumber)) return;
    
    const handleElementClick = (e) => {
      console.log('[Onboarding] Click detected on step', stepNumber);
      
      // Check if this click will cause navigation
      const nextStep = steps[currentStep + 1];
      const willNavigate = nextStep && nextStep.page !== window.location.pathname;
      
      if (willNavigate) {
        console.log('[Onboarding] Navigation click detected');
        const newStep = currentStep + 1;
        const stepId = currentStep + 1;
        
        localStorage.setItem('onboardingCurrentStep', newStep.toString());
        
        if (MILESTONES[stepId]) {
          trackOnboarding('milestone_reached', MILESTONES[stepId], stepId);
        }
      } else {
        console.log('[Onboarding] Non-navigation click, advancing after 250ms');
        setTimeout(() => {
          handleNext();
        }, 250);
      }
    };
    
    highlightedElement.addEventListener('click', handleElementClick);
    
    return () => {
      highlightedElement.removeEventListener('click', handleElementClick);
    };
  }, [highlightedElement, currentStep, isActive, steps]);

  // Navigate to correct page and highlight element
  useEffect(() => {
    if (!pageReady) {
      console.log('[Onboarding] Waiting for page to be ready before highlighting...');
      return;
    }

    if (isActive && currentStep < steps.length) {
      const step = steps[currentStep];
      
      if (highlightedElement) {
        const computedStyle = window.getComputedStyle(highlightedElement);
        const originalZ = computedStyle.zIndex;
        const originalPos = computedStyle.position;
        
        if (originalZ === '9999') {
          highlightedElement.style.zIndex = '';
        }
        if (originalPos === 'relative' && highlightedElement.style.position === 'relative') {
          highlightedElement.style.position = '';
        }
      }
      
      localStorage.setItem('onboardingCurrentStep', currentStep.toString());
      
      if (step.page !== window.location.pathname) {
        setHighlightedElement(null);
        return;
      }
      
      if (step.target) {
        const timer = setTimeout(() => {
          if (step.requiresModalOpen && step.modalTrigger) {
            const modalButton = document.querySelector(step.modalTrigger) || 
                               Array.from(document.querySelectorAll('button')).find(btn => 
                                 btn.textContent.includes('ADD USERS')
                               );
            
            if (modalButton) {
              modalButton.click();
              
              setTimeout(() => {
                findAndHighlightElement();
              }, 500);
              return;
            }
          }
          
          findAndHighlightElement();
          
          function findAndHighlightElement() {
            let element = null;
            const selectors = step.target.split(',').map(s => s.trim());
            
            for (const selector of selectors) {
              if (selector.includes(':contains')) {
                const match = selector.match(/(.+):contains\("(.+)"\)/);
                if (match) {
                  const [, baseSelector, text] = match;
                  const elements = document.querySelectorAll(baseSelector);
                  element = Array.from(elements).find(el => 
                    el.textContent.trim().includes(text)
                  );
                }
              } else {
                element = document.querySelector(selector);
              }
              
              if (element) break;
            }
            
            if (element) {
              console.log('[Onboarding] Element found and highlighting:', step.target);
              setHighlightedElement(element);
              element.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center',
                inline: 'center' 
              });
            } else {
              console.warn(`[Onboarding] Target element not found: ${step.target}`);
              setHighlightedElement(null);
            }
          }
        }, 100);
        
        return () => clearTimeout(timer);
      } else {
        setHighlightedElement(null);
      }
    }
  }, [currentStep, isActive, steps, pageReady]);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      const stepId = currentStep + 1;
      console.log('[Onboarding] Moving to step:', nextStep);
      setCurrentStep(nextStep);
      
      if (MILESTONES[stepId]) {
        trackOnboarding('milestone_reached', MILESTONES[stepId], stepId);
      }

      localStorage.setItem('onboardingCurrentStep', nextStep.toString());
    } else {
      console.log('[Onboarding] Tour completed');
      trackOnboarding('completed', 'finished_onboarding', 34);
      localStorage.setItem('onboardingCompleted', 'true');
      localStorage.removeItem('onboardingCurrentStep');
      onComplete();
    }
  }, [currentStep, steps, onComplete]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    const stepId = currentStep + 1;
    localStorage.setItem('onboardingCompleted', 'true');
    localStorage.removeItem('onboardingCurrentStep');
    trackOnboarding('skipped', null, stepId);
    onSkip();
  }, [currentStep, onSkip]);

  const handleSkipToStep = useCallback((targetStep, samePageSkip = false) => {
    if (samePageSkip) {
      console.log('[Onboarding] Same-page skip to step:', targetStep);
      setCurrentStep(targetStep);
      localStorage.setItem('onboardingCurrentStep', targetStep.toString());
    }
    // Navigation-based skips are handled in DraggableTooltip
  }, []);

  // Dragging handlers
  const handleMouseDown = (e) => {
    if (e.target.closest('.drag-handle')) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - tooltipPosition.x,
        y: e.clientY - tooltipPosition.y,
      });
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setTooltipPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    }
  }, [isDragging, dragOffset]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  if (!isActive || currentStep >= steps.length) {
    return null;
  }

  const step = steps[currentStep];
  const shouldPulse = CLICK_TO_ADVANCE_STEPS.includes(currentStep + 1) || step.requiresClick === false;
  const isNextDisabled = CLICK_TO_ADVANCE_STEPS.includes(currentStep + 1) || step.isNextDisabled;
  
  return (
    <>
      <SpotlightOverlay 
        highlightedElement={highlightedElement} 
        shouldPulse={shouldPulse}
      />
      <DraggableTooltip
        step={step}
        currentStep={currentStep + 1}
        totalSteps={steps.length}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onSkip={handleSkip}
        position={tooltipPosition}
        onMouseDown={handleMouseDown}
        isDragging={isDragging}
        isNextDisabled={isNextDisabled}
        onSkipToStep={handleSkipToStep}
      />
    </>
  );
};

// Helper function to wait for loader to disappear
const waitForLoader = async () => {
  return new Promise((resolve) => {
    const checkLoader = () => {
      const loader = document.querySelector('.spinner-border, .loading, .loader, [class*="loading"], [class*="spinner"]');
      
      if (!loader) {
        console.log('[Onboarding] No loader found, proceeding immediately');
        resolve();
        return;
      }
      
      console.log('[Onboarding] Loader detected, waiting for it to disappear...');
      
      const observer = new MutationObserver((mutations) => {
        const loaderStillExists = document.querySelector('.spinner-border, .loading, .loader, [class*="loading"], [class*="spinner"]');
        if (!loaderStillExists) {
          console.log('[Onboarding] Loader disappeared, ready to highlight');
          observer.disconnect();
          resolve();
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      setTimeout(() => {
        console.log('[Onboarding] Loader timeout, proceeding anyway');
        observer.disconnect();
        resolve();
      }, 5000);
    };
    
    checkLoader();
  });
};

// Spotlight overlay component with pulsing animation
const SpotlightOverlay = ({ highlightedElement, shouldPulse }) => {
  const [elementRect, setElementRect] = useState(null);

  useEffect(() => {
    if (highlightedElement) {
      const originalZIndex = highlightedElement.style.zIndex;
      const originalPosition = highlightedElement.style.position;
      const computedStyle = window.getComputedStyle(highlightedElement);
      const wasStatic = computedStyle.position === 'static';
      
      highlightedElement.style.zIndex = '9999';
      if (wasStatic) {
        highlightedElement.style.position = 'relative';
      }
      
      const updateRect = () => {
        const rect = highlightedElement.getBoundingClientRect();
        setElementRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
      };

      updateRect();
      
      window.addEventListener('scroll', updateRect, true);
      window.addEventListener('resize', updateRect);
      
      return () => {
        highlightedElement.style.zIndex = originalZIndex;
        highlightedElement.style.position = originalPosition;
        
        window.removeEventListener('scroll', updateRect, true);
        window.removeEventListener('resize', updateRect);
      };
    } else {
      setElementRect(null);
    }
  }, [highlightedElement]);

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9998,
          pointerEvents: 'none',
        }}
      />

      {elementRect && (
        <>
          <style>
            {`
              @keyframes pulse-border {
                0%, 100% {
                  transform: scale(1);
                  opacity: 1;
                }
                50% {
                  transform: scale(1.05);
                  opacity: 0.8;
                }
              }
              
              @keyframes pulse-shadow {
                0%, 100% {
                  box-shadow: 0 0 0 4px rgba(26, 188, 156, 0.3), 0 0 20px rgba(26, 188, 156, 0.4);
                }
                50% {
                  box-shadow: 0 0 0 8px rgba(26, 188, 156, 0.2), 0 0 30px rgba(26, 188, 156, 0.6);
                }
              }
            `}
          </style>
          <div
            style={{
              position: 'fixed',
              top: elementRect.top - 8,
              left: elementRect.left - 8,
              width: elementRect.width + 16,
              height: elementRect.height + 16,
              border: '3px solid #1abc9c',
              borderRadius: '8px',
              zIndex: 10000,
              pointerEvents: 'none',
              animation: shouldPulse 
                ? 'pulse-border 2s ease-in-out infinite, pulse-shadow 2s ease-in-out infinite' 
                : 'none',
              transition: 'all 0.3s ease',
            }}
          />
        </>
      )}
    </>
  );
};

// Draggable tooltip component
const DraggableTooltip = ({
  step,
  currentStep,
  totalSteps,
  onNext,
  onPrevious,
  onSkip,
  position,
  onMouseDown,
  isDragging,
  isNextDisabled,
  onSkipToStep,
}) => {
  
  const handleSkipToStep = () => {
    if (step.skipOption) {
      // Check if it's a same-page skip
      if (step.skipOption.samePageSkip) {
        console.log('[Onboarding] Same-page skip to step:', step.skipOption.targetStep);
        onSkipToStep(step.skipOption.targetStep, true);
        return;
      }
      
      // Navigation-based skip
      let skipTarget = document.querySelector(step.skipOption.targetSelector);
      
      if (!skipTarget) {
        const allLinks = document.querySelectorAll('a, nav a');
        skipTarget = Array.from(allLinks).find(el => 
          el.textContent.trim().includes('HOST MONITORING') || 
          el.textContent.trim().includes('Host Monitoring')
        );
      }
      
      if (skipTarget) {
        console.log('[Onboarding] Skip button clicked, navigating to:', step.skipOption.targetStep);
        localStorage.setItem('onboardingCurrentStep', step.skipOption.targetStep.toString());
        skipTarget.click();
      } else {
        console.warn('[Onboarding] Skip target not found');
      }
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: '400px',
        zIndex: 10000,
        pointerEvents: 'auto',
        cursor: isDragging ? 'grabbing' : 'default',
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
        }}
      >
        <div
          className="drag-handle"
          onMouseDown={onMouseDown}
          style={{
            backgroundColor: '#343a40',
            color: '#fff',
            padding: '15px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'grab',
          }}
        >
          <h5 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color:'#fff' }}>
            {step.title}
          </h5>
          <button
            onClick={onSkip}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#fff',
              fontSize: '24px',
              cursor: 'pointer',
              padding: 0,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '20px' }}>
          <div 
            className="onboarding-message"
            style={{ 
              margin: '0 0 20px 0', 
              color: '#666', 
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap'
            }}
            dangerouslySetInnerHTML={{ __html: parseMessageToHTML(step.message) }}
          />

          {step.skipOption && (
            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
              <button
                onClick={handleSkipToStep}
                style={{
                  padding: '8px 16px',
                  border: '2px solid #1abc9c',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  color: '#1abc9c',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                }}
              >
                {step.skipOption.buttonText || 'Skip Error Details'}
              </button>
            </div>
          )}

          <div
            style={{
              fontSize: '12px',
              color: '#999',
              marginBottom: '15px',
              textAlign: 'center',
            }}
          >
            Step {currentStep} of {totalSteps}
          </div>

          <div
            style={{
              height: '4px',
              backgroundColor: '#e0e0e0',
              borderRadius: '2px',
              marginBottom: '20px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                backgroundColor: '#1abc9c',
                width: `${(currentStep / totalSteps) * 100}%`,
                transition: 'width 0.3s ease',
              }}
            />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '10px',
            }}
          >
            <button
              onClick={onPrevious}
              disabled={currentStep === 1}
              style={{
                flex: 1,
                padding: '8px 16px',
                border: '1px solid #6c757d',
                borderRadius: '4px',
                backgroundColor: '#fff',
                color: '#6c757d',
                cursor: currentStep === 1 ? 'not-allowed' : 'pointer',
                opacity: currentStep === 1 ? 0.5 : 1,
              }}
            >
              Previous
            </button>
            <button
              onClick={onSkip}
              style={{
                flex: 1,
                padding: '8px 16px',
                border: '1px solid #6c757d',
                borderRadius: '4px',
                backgroundColor: '#fff',
                color: '#6c757d',
                cursor: 'pointer',
              }}
            >
              Skip Tour
            </button>
            <button
              onClick={onNext}
              disabled={isNextDisabled}
              style={{
                flex: 1,
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: isNextDisabled ? '#ccc' : '#1abc9c',
                color: '#fff',
                cursor: isNextDisabled ? 'not-allowed' : 'pointer',
                opacity: isNextDisabled ? 0.6 : 1,
                fontWeight: '500',
              }}
            >
              {currentStep === totalSteps ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;